import { CommonValidator } from '../utils/commonValidator';
import { EncoderToolType, EncodingOperation, CertificateMode, CertificateFormat } from '../types/EncodingOperation';
import type { CertificateConfiguration } from '../types';
import forge from 'node-forge';

export class CertificateService {
  /**
   * Creates a certificate configuration
   */
  static createConfiguration(
    input: string | Uint8Array,
    mode: CertificateMode = CertificateMode.DECODE,
    options: {
      inputFormat?: CertificateFormat;
      outputFormat?: CertificateFormat;
      includeExtensions?: boolean;
      validateSignature?: boolean;
      checkRevocation?: boolean;
      password?: string; // For PKCS12 files
    } = {}
  ): CertificateConfiguration {
    return {
      input,
      mode,
      inputFormat: options.inputFormat,
      outputFormat: options.outputFormat ?? CertificateFormat.PEM,
      includeExtensions: options.includeExtensions ?? true,
      validateSignature: options.validateSignature ?? false,
      checkRevocation: options.checkRevocation ?? false,
      password: options.password
    };
  }

  /**
   * Validates certificate configuration
   */
  static validateConfiguration(config: CertificateConfiguration): void {
    if (!config.input) {
      throw new Error('Certificate data is required');
    }

    if (!Object.values(CertificateMode).includes(config.mode)) {
      throw new Error('Invalid mode specified');
    }

    if (config.inputFormat && !Object.values(CertificateFormat).includes(config.inputFormat)) {
      throw new Error('Invalid input format');
    }

    if (config.outputFormat && !Object.values(CertificateFormat).includes(config.outputFormat)) {
      throw new Error('Invalid output format');
    }
  }

  /**
   * Decodes certificate data using appropriate parser
   */
  static async decode(config: CertificateConfiguration): Promise<EncodingOperation> {
    try {
      this.validateConfiguration(config);

      const validation = CommonValidator.validateText(
        typeof config.input === 'string' ? config.input : new TextDecoder().decode(config.input),
        CommonValidator.getValidationRules(EncoderToolType.CERTIFICATE_DECODER)
      );

      if (!validation.isValid) {
        return {
          id: this.generateOperationId(),
          status: 'error',
          errorMessage: validation.errors[0] || 'Invalid certificate data',
          operationType: 'decode',
          toolType: EncoderToolType.CERTIFICATE_DECODER,
          timestamp: new Date(),
          inputData: typeof config.input === 'string' ? config.input : 'binary data'
        };
      }

      const detectedFormat = config.inputFormat || this.detectFormat(config.input);

      if (!detectedFormat) {
        throw new Error('Unable to detect certificate format');
      }

      // Delegate to appropriate parser
      switch (detectedFormat) {
        case CertificateFormat.PKCS12:
          return this.decodePKCS12(config);
        case CertificateFormat.PEM:
        case CertificateFormat.DER:
        case CertificateFormat.BASE64:
          return this.decodeX509(config, detectedFormat);
        default:
          throw new Error(`Unsupported certificate format: ${detectedFormat}`);
      }

    } catch (error) {
      return {
        id: this.generateOperationId(),
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
        operationType: 'decode',
        toolType: EncoderToolType.CERTIFICATE_DECODER,
        timestamp: new Date(),
        inputData: typeof config.input === 'string' ? config.input : 'binary data'
      };
    }
  }

  /**
   * Decodes X.509 certificates (PEM/DER/Base64) using @peculiar/x509
   */
  private static async decodeX509(config: CertificateConfiguration, format: CertificateFormat): Promise<EncodingOperation> {
    try {
      // Use @peculiar/x509 for X.509 certificates as specified in research.md
      const { X509Certificate } = await import('@peculiar/x509');

      const inputData = typeof config.input === 'string' ? config.input : new TextDecoder().decode(config.input);
      let cert;

      switch (format) {
        case CertificateFormat.PEM:
          cert = new X509Certificate(inputData);
          break;

        case CertificateFormat.DER:
        case CertificateFormat.BASE64:
          // Convert base64 to Uint8Array for DER format
          const base64Data = inputData.replace(/\s+/g, '');
          const derBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
          cert = new X509Certificate(derBytes);
          break;

        default:
          throw new Error(`Unsupported X.509 format: ${format}`);
      }

      
      // Extract certificate information using @peculiar/x509 API
      const certificateInfo = {
        subject: this.formatRDN(cert.subject),
        issuer: this.formatRDN(cert.issuer),
        serialNumber: cert.serialNumber,
        version: 'v3', // @peculiar/x509 doesn't expose version directly
        notBefore: cert.notBefore,
        notAfter: cert.notAfter,
        signatureAlgorithm: cert.signatureAlgorithm?.name || 'Unknown',
        publicKey: {
          algorithm: cert.publicKey?.algorithm?.name || 'Unknown',
          keySize: this.getKeySize(cert.publicKey)
        },
        fingerprint: await this.calculateX509Fingerprint(cert)
      };

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.CERTIFICATE_DECODER,
        operationType: 'decode',
        inputData: inputData,
        outputData: JSON.stringify(certificateInfo, null, 2),
        timestamp: new Date(),
        status: 'success',
        metadata: {
          format,
          ...certificateInfo
        }
      };

    } catch (error) {
      return {
        id: this.generateOperationId(),
        status: 'error',
        errorMessage: this.getUserFriendlyErrorMessage(error instanceof Error ? error.message : 'Unknown error'),
        operationType: 'decode',
        toolType: EncoderToolType.CERTIFICATE_DECODER,
        timestamp: new Date(),
        inputData: typeof config.input === 'string' ? config.input : 'binary data'
      };
    }
  }

  /**
   * Format RDN (Relative Distinguished Name) to string
   */
  private static formatRDN(rdn: any): string {
    try {
      // @peculiar/x509 subject/issuer might be a string or object
      if (typeof rdn === 'string') {
        return rdn || 'N/A';
      }

      // If it's an object with items property
      if (rdn && rdn.items && Array.isArray(rdn.items)) {
        const items = rdn.items.map((item: any) => {
          if (item.type && item.value) {
            const oid = item.type;
            const value = Array.isArray(item.value) ? item.value.join(', ') : item.value;

            // Map common OIDs to readable names
            const oidNames: Record<string, string> = {
              '2.5.4.3': 'CN',
              '2.5.4.6': 'C',
              '2.5.4.7': 'L',
              '2.5.4.8': 'ST',
              '2.5.4.10': 'O',
              '2.5.4.11': 'OU'
            };

            const name = oidNames[oid] || oid;
            return `${name}=${value}`;
          }
          return '';
        }).filter(Boolean);

        return items.join(', ') || 'N/A';
      }

      // If it's an array of items
      if (rdn && Array.isArray(rdn)) {
        const items = rdn.map((item: any) => {
          if (item.type && item.value) {
            const oid = item.type;
            const value = Array.isArray(item.value) ? item.value.join(', ') : item.value;

            const oidNames: Record<string, string> = {
              '2.5.4.3': 'CN',
              '2.5.4.6': 'C',
              '2.5.4.7': 'L',
              '2.5.4.8': 'ST',
              '2.5.4.10': 'O',
              '2.5.4.11': 'OU'
            };

            const name = oidNames[oid] || oid;
            return `${name}=${value}`;
          }
          return '';
        }).filter(Boolean);

        return items.join(', ') || 'N/A';
      }

      // If it has a direct string representation
      if (rdn && rdn.toString && typeof rdn.toString === 'function') {
        const str = rdn.toString();
        return str !== '[object Object]' ? str : 'N/A';
      }

      return 'N/A';
    } catch (error) {
      console.error('Error formatting RDN:', error, rdn);
      return 'N/A';
    }
  }

  /**
   * Get key size from public key
   */
  private static getKeySize(publicKey: any): number | undefined {
    try {
      if (publicKey.algorithm && publicKey.algorithm.namedCurve) {
        // For EC keys, return curve size in bits
        const curveSizes: Record<string, number> = {
          'P-256': 256,
          'P-384': 384,
          'P-521': 521,
          'secp256r1': 256,
          'secp384r1': 384,
          'secp521r1': 521
        };
        return curveSizes[publicKey.algorithm.namedCurve];
      }

      // For RSA keys, check if modulus is available
      if (publicKey.algorithm && publicKey.algorithm.name === 'RSASSA-PKCS1-v1_5') {
        // @peculiar/x509 doesn't expose modulus directly, so we'll return undefined
        return undefined;
      }

      return undefined;
    } catch (error) {
      return undefined;
    }
  }

  /**
   * Calculate fingerprint using WebCrypto API
   */
  private static async calculateX509Fingerprint(cert: any): Promise<{
    sha1: string;
    sha256: string;
    md5: string;
  }> {
    try {
      // Use native WebCrypto API available in browsers
      const crypto = window.crypto || (globalThis as any).crypto;

      // Get the raw certificate data
      const certRaw = cert.rawData;

      // Calculate SHA-256
      const sha256Buffer = await crypto.subtle.digest('SHA-256', certRaw);
      const sha256Hex = Array.from(new Uint8Array(sha256Buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // Calculate SHA-1
      const sha1Buffer = await crypto.subtle.digest('SHA-1', certRaw);
      const sha1Hex = Array.from(new Uint8Array(sha1Buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // MD5 is not available in WebCrypto, so we'll use a fallback
      return {
        sha1: sha1Hex,
        sha256: sha256Hex,
        md5: 'unsupported'
      };
    } catch (error) {
      return {
        sha1: 'unknown',
        sha256: 'unknown',
        md5: 'unknown'
      };
    }
  }

  private static getUserFriendlyErrorMessage(errorMsg: string): string {
    if (errorMsg.includes('atob') || errorMsg.includes('Base64')) {
      return 'Invalid Base64 format. Please ensure the certificate is correctly encoded.';
    } else if (errorMsg.includes('password') && errorMsg.includes('PKCS12')) {
      return 'Password required for PKCS12 file. Please enter the correct password.';
    } else if (errorMsg.includes('ASN1 schema') || errorMsg.includes('Certificate ASN1')) {
      return 'The PKCS12 file structure is not recognized. This may be a password-protected file or an unsupported format. For password-protected PKCS12 files, specialized parsing libraries are required.';
    } else if (errorMsg.includes('No certificates found in PKCS12')) {
      return 'No certificates could be extracted from the PKCS12 file. The file may be password-protected, corrupted, or in an unsupported format.';
    } else if (errorMsg.includes('does not appear to be a valid PKCS12')) {
      return 'The provided data does not appear to be a valid PKCS12 file. Please check that you have uploaded the correct file.';
    } else if (errorMsg.includes('malformed')) {
      return 'The certificate appears to be malformed. Please check the input data.';
    } else if (errorMsg.includes('Unsupported certificate format')) {
      return 'Unsupported certificate format detected. Please check the file type.';
    } else {
      return `An error occurred while processing the certificate: ${errorMsg}`;
    }
  }

  /**
   * Decodes PKCS12 format using @peculiar/x509
   * Note: Full PKCS12 parsing requires specialized libraries for password-protected files
   * This implementation handles basic PKCS12 files without password protection
   */
  private static async decodePKCS12(config: CertificateConfiguration): Promise<EncodingOperation> {
    try {
      const { X509Certificate } = await import('@peculiar/x509');

      // Handle password-protected PKCS12 using node-forge
      if (config.password) {
        // Convert input to Uint8Array
        let pkcs12Bytes: Uint8Array;
        if (typeof config.input === 'string') {
          const cleanData = config.input
            .replace(/-----BEGIN PKCS12-----|-----END PKCS12-----|\s/g, '');
          if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanData)) {
            throw new Error('Invalid PKCS12 Base64 format');
          }
          pkcs12Bytes = Uint8Array.from(atob(cleanData), c => c.charCodeAt(0));
        } else {
          pkcs12Bytes = config.input;
        }

        // Parse with node-forge
        try {
          const derString = forge.util.binary.raw.encode(pkcs12Bytes);
          const p12Asn1 = forge.asn1.fromDer(derString);
          const p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, config.password);

          // Extract certificates
          const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
          const certs = certBags[forge.pki.oids.certBag] || [];

          if (certs.length === 0) {
            throw new Error('No certificates found in PKCS12 file after decryption');
          }

          // Convert forge certificate to @peculiar/x509 format for consistent parsing
          const forgeCert = certs[0].cert;
          if (!forgeCert) {
            throw new Error('Invalid certificate found in PKCS12 file');
          }
          const pemCert = forge.pki.certificateToPem(forgeCert);
          const cert = new X509Certificate(pemCert);

          // Extract certificate information using @peculiar/x509 API
          const certificateInfo = {
            subject: this.formatRDN(cert.subject),
            issuer: this.formatRDN(cert.issuer),
            serialNumber: cert.serialNumber,
            version: 'v3',
            notBefore: cert.notBefore,
            notAfter: cert.notAfter,
            signatureAlgorithm: cert.signatureAlgorithm?.name || 'Unknown',
            publicKey: {
              algorithm: cert.publicKey?.algorithm?.name || 'Unknown',
              keySize: this.getKeySize(cert.publicKey)
            },
            fingerprint: await this.calculateX509Fingerprint(cert)
          };

          return {
            id: this.generateOperationId(),
            toolType: EncoderToolType.CERTIFICATE_DECODER,
            operationType: 'decode',
            inputData: typeof config.input === 'string' ? config.input : 'binary data',
            outputData: JSON.stringify(certificateInfo, null, 2),
            timestamp: new Date(),
            status: 'success',
            metadata: {
              format: CertificateFormat.PKCS12,
              certificateCount: certs.length,
              primaryCertificate: certificateInfo,
              pkcs12Note: 'Successfully decrypted password-protected PKCS12 file'
            }
          };
        } catch (forgeError) {
          throw new Error(`PKCS12 decryption failed: ${forgeError instanceof Error ? forgeError.message : 'Unknown error'}`);
        }
      }

      // Existing code for non-password protected PKCS12
      const inputData = typeof config.input === 'string' ? config.input : new TextDecoder().decode(config.input);

      // Clean base64 data and remove PEM headers if present
      const cleanData = inputData
        .replace(/-----BEGIN PKCS12-----|-----END PKCS12-----|\s/g, '');

      // Validate base64 format
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanData)) {
        throw new Error('Invalid PKCS12 Base64 format');
      }

      // Convert base64 to Uint8Array
      let pkcs12Bytes: Uint8Array;
      try {
        pkcs12Bytes = Uint8Array.from(atob(cleanData), c => c.charCodeAt(0));
      } catch (error) {
        throw new Error('Failed to decode PKCS12 Base64 data');
      }

      // Check if this looks like a PKCS12 file (PFX structure)
      if (!this.looksLikePKCS12Binary(pkcs12Bytes)) {
        throw new Error('Data does not appear to be a valid PKCS12 file');
      }

      // Try to find certificate data within the PKCS12 structure
      const certPatterns = this.findCertificatePatterns(pkcs12Bytes);

      if (certPatterns.length === 0) {
        throw new Error('No certificates found in PKCS12 file. The file may be password-protected or corrupted.');
      }

      // Try to parse each found certificate pattern
      let cert: any = null;
      let parseError: Error | null = null;

      for (const pattern of certPatterns) {
        try {
          const arrayBuffer = pattern.buffer.slice(pattern.byteOffset, pattern.byteOffset + pattern.byteLength) as ArrayBuffer;
          cert = new X509Certificate(arrayBuffer);
          // If we get here, parsing succeeded
          break;
        } catch (error) {
          parseError = error as Error;
          continue; // Try next pattern
        }
      }

      if (!cert) {
        throw new Error(`Failed to parse certificates from PKCS12 file: ${parseError?.message || 'Unknown parsing error'}`);
      }

      
      // Extract certificate information using @peculiar/x509 API
      const certificateInfo = {
        subject: this.formatRDN(cert.subject),
        issuer: this.formatRDN(cert.issuer),
        serialNumber: cert.serialNumber,
        version: 'v3', // @peculiar/x509 doesn't expose version directly
        notBefore: cert.notBefore,
        notAfter: cert.notAfter,
        signatureAlgorithm: cert.signatureAlgorithm?.name || 'Unknown',
        publicKey: {
          algorithm: cert.publicKey?.algorithm?.name || 'Unknown',
          keySize: this.getKeySize(cert.publicKey)
        },
        fingerprint: await this.calculateX509Fingerprint(cert)
      };

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.CERTIFICATE_DECODER,
        operationType: 'decode',
        inputData: inputData,
        outputData: JSON.stringify(certificateInfo, null, 2),
        timestamp: new Date(),
        status: 'success',
        metadata: {
          format: CertificateFormat.PKCS12,
          certificateCount: certPatterns.length,
          primaryCertificate: certificateInfo,
          pkcs12Note: config.password
            ? 'Note: Password-protected PKCS12 files require specialized parsing libraries. This is a best-effort extraction.'
            : 'Certificate extracted from PKCS12 container. Full container parsing requires additional ASN.1 structure handling.'
        }
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        id: this.generateOperationId(),
        status: 'error',
        errorMessage: this.getUserFriendlyErrorMessage(errorMessage),
        operationType: 'decode',
        toolType: EncoderToolType.CERTIFICATE_DECODER,
        timestamp: new Date(),
        inputData: typeof config.input === 'string' ? config.input : 'binary data'
      };
    }
  }

  /**
   * Find X.509 certificate patterns in binary data
   */
  private static findCertificatePatterns(data: Uint8Array): Uint8Array[] {
    const certificates: Uint8Array[] = [];

    // X.509 certificates start with ASN.1 sequence tag: 0x30
    for (let i = 0; i < data.length - 10; i++) {
      if (data[i] === 0x30) {
        // Try to determine the length of this ASN.1 structure
        let length = 0;
        let headerSize = 0;

        if (data[i + 1] < 0x80) {
          // Short form length
          length = data[i + 1];
          headerSize = 2;
        } else if (data[i + 1] === 0x81 && i + 2 < data.length) {
          // Long form - 1 byte length
          length = data[i + 2];
          headerSize = 3;
        } else if (data[i + 1] === 0x82 && i + 3 < data.length) {
          // Long form - 2 byte length
          length = (data[i + 2] << 8) | data[i + 3];
          headerSize = 4;
        } else if (data[i + 1] === 0x83 && i + 4 < data.length) {
          // Long form - 3 byte length
          length = (data[i + 2] << 16) | (data[i + 3] << 8) | data[i + 4];
          headerSize = 5;
        } else {
          continue; // Skip invalid length encoding
        }

        // Check if this looks like a reasonable certificate size
        const totalSize = length + headerSize;
        if (length > 100 && length < 20000 && i + totalSize <= data.length) {
          const certData = data.slice(i, i + totalSize);
          certificates.push(certData);
          i += totalSize - 1; // Skip to end of this certificate
        }
      }
    }

    return certificates;
  }

  
  /**
   * Main processing function
   */
  static async process(config: CertificateConfiguration): Promise<EncodingOperation> {
    return this.decode(config);
  }

  /**
   * Detects certificate format from data
   */
  static detectFormat(data: string | Uint8Array): CertificateFormat | null {
    if (typeof data === 'string') {
      // Check for PEM format
      if (data.includes('-----BEGIN CERTIFICATE-----')) {
        return CertificateFormat.PEM;
      }

      // Check for PKCS12 format
      if (data.includes('-----BEGIN PKCS12-----') || this.looksLikePKCS12Base64(data)) {
        return CertificateFormat.PKCS12;
      }

      // Check for PKCS7 format
      if (data.includes('-----BEGIN PKCS7-----')) {
        return CertificateFormat.PKCS7;
      }

      // Check for Base64 format
      if (/^[A-Za-z0-9+/]*={0,2}$/.test(data.trim())) {
        return CertificateFormat.BASE64;
      }

      // Check for hex format (DER)
      if (/^[0-9a-fA-F]+$/.test(data.trim())) {
        return CertificateFormat.DER;
      }
    } else {
      // Check binary data for PKCS12 magic bytes
      if (this.looksLikePKCS12Binary(data)) {
        return CertificateFormat.PKCS12;
      }
      // For binary data, assume DER format for other cases
      return CertificateFormat.DER;
    }

    return null;
  }

  /**
   * Checks if base64 string looks like PKCS12
   */
  private static looksLikePKCS12Base64(data: string): boolean {
    try {
      const decoded = Uint8Array.from(atob(data.trim()), c => c.charCodeAt(0));
      return this.looksLikePKCS12Binary(decoded);
    } catch {
      return false;
    }
  }

  /**
   * Checks if binary data looks like PKCS12 by checking magic bytes
   */
  private static looksLikePKCS12Binary(data: Uint8Array): boolean {
    // PKCS#12 files start with specific ASN.1 sequence tags
    if (data.length < 4) return false;

    // Check for ASN.1 sequence tag (0x30)
    if (data[0] !== 0x30) return false;

    // Check length encoding
    const lengthByte = data[1];
    if (lengthByte === 0x82 && data.length > 3) {
      // Long form length (2 bytes)
      const length = (data[2] << 8) | data[3];
      return length === data.length - 4;
    } else if (lengthByte === 0x81 && data.length > 2) {
      // Long form length (1 byte)
      const length = data[2];
      return length === data.length - 3;
    } else if (lengthByte < 0x80) {
      // Short form length
      return length === data.length - 2;
    }

    // For other cases, be more permissive
    return data[1] === 0x82 || data[1] === 0x80 || data[1] > 0x81;
  }

  
  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `certificate-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Gets supported certificate formats
   */
  static getSupportedFormats(): Array<{ format: string; description: string; extensions: string[] }> {
    return [
      {
        format: 'PEM',
        description: 'Privacy Enhanced Mail format with ASCII armor',
        extensions: ['.pem', '.crt', '.cer']
      },
      {
        format: 'DER',
        description: 'Distinguished Encoding Rules format (binary)',
        extensions: ['.der']
      },
      {
        format: 'BASE64',
        description: 'Base64 encoded DER format',
        extensions: ['.b64']
      },
      {
        format: 'PKCS12',
        description: 'PKCS#12 format with private keys and certificates',
        extensions: ['.p12', '.pfx']
      },
      {
        format: 'PKCS7',
        description: 'PKCS#7 format for certificate chains',
        extensions: ['.p7b', '.p7c']
      }
    ];
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}